import { type HttpClient, query, segment } from '../http';

import {
  type CreateFamilyPayload,
  type Family,
  type FamilyInvitation,
  type ListFamilyPurchasesQuery,
  type FamilyInvitationLink,
  type FamilyRole,
  type FamilyInvitationLinkPreview,
  type ListFamilyPurchasesResult,
} from './families.types';

/**
 * Households: members, roles, invitations and joint purchases.
 *
 * @param http Transport supplied by the application.
 * @returns The `families` domain of the API client.
 */
export function createFamiliesApi(http: HttpClient) {
  return {
    /**
     * Reads the caller's household in one call.
     *
     * Warning: Answers `family.error.access.not_in_family` (400) when the caller has
     * no household. That is the **normal** state of someone who never created
     * one, not a failure: gate the call on `familyId`, or treat the error as a
     * state of the screen.
     *
     * @returns Members, statistics, top games and the purchases summary.
     */
    getMine: () => http.get<Family>('/families/me'),

    /**
     * Creates a household, with the caller as its founding administrator.
     *
     * Warning: The **only paid route of the module**: answers 403
     * `entitlement.error.access.feature_not_included` when the account does not
     * hold the `FAMILY` feature. Joining, reading and leaving stay free.
     *
     * @param payload The household name.
     * @returns The newly created household.
     */
    create: (payload: CreateFamilyPayload) =>
      http.post<Family>('/families', payload),

    /**
     * Lists every joint purchase, paginated.
     *
     * The screen shows the three most recent from `purchasesSummary.recent`;
     * this call is only for the detailed view.
     *
     * @param params Page and page size.
     * @returns One page of purchases with the total.
     */
    listPurchases: (params: ListFamilyPurchasesQuery = {}) =>
      http.get<ListFamilyPurchasesResult>(
        `/families/purchases${query({ page: params.page, limit: params.limit })}`,
      ),

    /**
     * Invites an account into the household (administrators only).
     *
     * The id comes from `GET /users/search`: a household invitation targets an
     * account, never an e-mail address.
     *
     * @param userId The account to invite.
     * @returns The pending invitation.
     */
    invite: (userId: string) =>
      http.post<FamilyInvitation>('/families/invitations', { userId }),

    /** @returns The pending invitations addressed to the caller. */
    listReceivedInvitations: () =>
      http.get<readonly FamilyInvitation[]>('/families/invitations/received'),

    /**
     * Lists every invitation the household has sent, whatever its status
     * (administrators only).
     *
     * @returns The invitations; `invitedUser` says who each one targets.
     */
    listSentInvitations: () =>
      http.get<readonly FamilyInvitation[]>('/families/invitations/sent'),

    /**
     * Issues a share link (administrators only).
     *
     * Warning: Single-use and valid seven days: one link opens exactly one seat. The
     * raw token exists only in this response and cannot be read back.
     *
     * @returns The link token and its expiry.
     */
    generateInvitationLink: () =>
      http.post<FamilyInvitationLink>('/families/invitations/link'),

    /**
     * Describes the household behind a link **without consuming it**.
     *
     * Opening the link does not join, and reloading the page does not burn the
     * invitation.
     *
     * @param token The raw link token.
     * @returns The household name and how many seats are left.
     */
    resolveInvitationLink: (token: string) =>
      http.get<FamilyInvitationLinkPreview>(
        `/families/invitations/link/${segment(token)}`,
      ),

    /**
     * Joins the household through a link, consuming it.
     *
     * @param token The raw link token.
     */
    acceptInvitationLink: (token: string) =>
      http.post<void>(`/families/invitations/link/${segment(token)}/accept`),

    /**
     * Joins the household that sent the invitation.
     *
     * @param id The invitation id.
     */
    acceptInvitation: (id: string) =>
      http.post<void>(`/families/invitations/${segment(id)}/accept`),

    /**
     * Declines an invitation.
     *
     * @param id The invitation id.
     */
    declineInvitation: (id: string) =>
      http.post<void>(`/families/invitations/${segment(id)}/decline`),

    /**
     * Cancels an invitation still pending (administrators only).
     *
     * @param id The invitation id.
     */
    cancelInvitation: (id: string) =>
      http.delete<void>(`/families/invitations/${segment(id)}`),

    /**
     * Promotes a member to administrator, or demotes one.
     *
     * Warning: The **founder** cannot be demoted: the server answers
     * `family.error.update_role.owner` (403).
     *
     * @param userId The member whose role changes.
     * @param role The role to apply.
     */
    updateMemberRole: (userId: string, role: FamilyRole) =>
      http.patch<void>(`/families/members/${segment(userId)}/role`, { role }),

    /**
     * Removes a member from the household.
     *
     * One route, two gestures: passing **your own** id is "leave" and needs no
     * right; passing someone else's is "remove" and is administrators only. An
     * administrator leaving promotes the oldest member in their place — unless
     * they founded the household, in which case it is dissolved.
     *
     * @param userId The member to remove.
     */
    removeMember: (userId: string) =>
      http.delete<void>(`/families/members/${segment(userId)}`),
  };
}

export type FamiliesApi = ReturnType<typeof createFamiliesApi>;
